import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { TimesheetEstimatedTimeFragment } from 'generated/resources/graphql'

import { dateToString } from 'modules/core/lib/dates'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { AggregatedWorkedTimeBlocks } from 'modules/attendance/lib/utils/AttendanceWorkedTimeUtils'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'

import Clocked from './Clocked'
import DefinitionBanner from './DefinitionBanner'
import Header from './Header'
import Legend from './Legend'
import Planned from './Planned'
import TimeCategories from './TimeCategories'
import WorkLocation from './WorkLocation'
import { calculateWorkedTime } from './lib/calculateWorkedTime'

interface Props {
  store: Store
  workedTimeForEmployee?: AggregatedWorkedTimeBlocks | null
  estimatedTimes: TimesheetEstimatedTimeFragment[]
}

const Worked = ({ store, workedTimeForEmployee, estimatedTimes }: Props) => {
  const { stringDateFrom: startOn, dateToOrToday } = useAttendanceDateNavigator()
  const { employeesSettings, shifts } = useTimesheetData()
  const hasLocationEnabled = EmployeesSettingsUtils.hasLocationEnabled(employeesSettings)

  const endOn = dateToString(dateToOrToday, 'backend')
  const workedTimeBlocks = workedTimeForEmployee?.workedTimeBlocks
  const scheduledOvertimeMinutes = store.estimatedOvertimeMinutesForRange

  const {
    minutes,
    breaksMinutes,
    unapprovedOvertimeMinutes,
    discardedOrAddedMinutes,
    trackedMinutes,
    estimatedMinutes,
    estimatedRegularMinutes,
    estimatedMinutesForCurrentPeriod,
  } = calculateWorkedTime({
    workedTime: workedTimeForEmployee,
    estimatedTimes,
    store,
  })

  const hideTemporallyBannerForEveryone = true

  return (
    <Box gap="s16" paddingX="s24">
      <Header
        minutes={minutes}
        estimatedMinutes={estimatedMinutes}
        startsOn={startOn}
        endsOn={endOn}
      />
      <Legend
        minutes={minutes}
        estimatedRegularMinutes={estimatedRegularMinutes}
        breaksMinutes={breaksMinutes}
      />
      <Box gap="s4">
        <Planned
          estimatedMinutes={estimatedMinutes}
          estimatedMinutesForCurrentPeriod={estimatedMinutesForCurrentPeriod}
          scheduledOvertimeMinutes={scheduledOvertimeMinutes}
        />
        <Clocked
          trackedMinutes={trackedMinutes}
          unapprovedOvertimeMinutes={unapprovedOvertimeMinutes}
          discardedOrAddedMinutes={discardedOrAddedMinutes}
        />
      </Box>
      <TimeCategories workedTimeBlocks={workedTimeBlocks} />
      {!hideTemporallyBannerForEveryone && <DefinitionBanner />}
      {hasLocationEnabled && <WorkLocation shifts={shifts} />}
    </Box>
  )
}

export default Worked
